import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiRequestError, isSessionRequiredError } from '../../api/client.js';
import { formatExpectedAt, formatMoney, formatQuantity, lowestEligibleOfferId, selectExpandedItem } from '../../domain/catalogRules.js';
import type { CanonicalCatalogItem, CatalogSearchResult, MoneyValue, QuantityValue, SupplierOffer, TenantFeatureMode } from '../../domain/models.js';
import { repository } from '../../repositories/index.js';
import { AlertIcon, ChevronIcon, SearchIcon } from '../../components/Icons.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { EmptyState } from '../../components/EmptyState.js';
import { useAuthSession } from '../auth/AuthSession.js';

function WallpaperThumb({ texture }: { texture: CanonicalCatalogItem['texture'] }) {
  const className = texture ? `wallpaper-thumb texture-${texture}` : 'wallpaper-thumb texture-neutral';
  return <span className={className} aria-label={texture ? undefined : 'Изображение товара отсутствует'} aria-hidden={texture ? 'true' : undefined}><span /></span>;
}

function priceMissingLabel(label: string, value: MoneyValue): string {
  if (value.state === 'source_invalid') return `${label}: источник вернул некорректное значение`;
  if (value.state === 'not_set') return `${label}: цена не задана`;
  return `${label}: цена не возвращена`;
}

function quantityMissingLabel(label: string, value: QuantityValue): string {
  if (value.state === 'source_error') return `${label}: ошибка источника`;
  if (value.state === 'expected_free') return `${label}: ожидается в поставке, сейчас не доступен`;
  if (value.state === 'not_expected') return `${label}: поставка не ожидается`;
  return `${label}: значение не возвращено`;
}

function OfferValue({ field, label, value, highlight = false, missingLabel, detail, detailTitle }: { field: 'purchase' | 'rrp' | 'free' | 'expected'; label: string; value: string; highlight?: boolean; missingLabel?: string; detail?: string | null; detailTitle?: string | null }) {
  return (
    <div className={highlight ? `offer-value offer-${field} lowest-price` : `offer-value offer-${field}`}>
      <span className="mobile-value-label">{label}</span>
      <strong aria-label={missingLabel && value === '—' ? missingLabel : undefined}>{value}</strong>
      {detail ? <span className="offer-value-detail" title={detailTitle ?? undefined}>{detail}</span> : null}
      {highlight ? <span className="lowest-note">минимальная актуальная</span> : null}
    </div>
  );
}

function OfferRow({ offer, lowestId }: { offer: SupplierOffer; lowestId: string | null }) {
  const isLowest = offer.id === lowestId;
  const exactTimestamp = offer.observedAt
    ? `Точное время обновления: ${offer.observedAt}.`
    : `Последнее успешное обновление: ${offer.lastSuccessAt ?? 'не указано'}.`;
  return (
    <div className="offer-row" role="row" data-offer-id={offer.id} data-freshness={offer.freshness} data-free-state={offer.free.state} data-purchase-state={offer.purchase.state}>
      <div className="offer-supplier" role="cell">
        <strong>{offer.supplierName}</strong>
        <span>{offer.supplierSku}</span>
      </div>
      <div className="offer-warehouse" role="cell">
        <strong>{offer.warehouse}</strong>
        {offer.lot ? <span className="offer-lot" data-testid={`lot-${offer.id}`}>{offer.lot}</span> : null}
      </div>
      <OfferValue field="purchase" label="Зак." value={formatMoney(offer.purchase)} highlight={isLowest} missingLabel={priceMissingLabel('Закупочная цена', offer.purchase)} />
      <OfferValue field="rrp" label="РРЦ" value={formatMoney(offer.rrp)} missingLabel={priceMissingLabel('Рекомендованная розничная цена', offer.rrp)} />
      <OfferValue field="free" label="Св." value={formatQuantity(offer.free)} missingLabel={quantityMissingLabel('Свободный остаток', offer.free)} />
      <OfferValue field="expected" label="Ож." value={formatQuantity(offer.expected)} missingLabel={quantityMissingLabel('Ожидаемое количество', offer.expected)} detail={formatExpectedAt(offer.expected.expectedAt)} detailTitle={offer.expected.expectedAt} />
      <div className="offer-status" role="cell">
        <StatusBadge
          state={offer.freshness}
          label={offer.freshnessLabel}
          detail={`${offer.freshnessDetail} ${exactTimestamp}`}
          compact
          phoneCompact
          testId={`status-${offer.id}`}
        />
        <span className="status-time">{offer.observedAt ?? `успешно ${offer.lastSuccessAt ?? '—'}`}</span>
      </div>
    </div>
  );
}

function OffersPanel({ item }: { item: CanonicalCatalogItem }) {
  const state = item.offersState ?? (item.offers.length > 0 ? 'loaded' : 'not_loaded');
  const lowestId = lowestEligibleOfferId(item.offers);
  return (
    <section className="offers-panel" aria-label={`Предложения поставщиков для ${item.article ?? item.name}`} data-testid={`offers-${item.id}`} data-offers-state={state}>
      <div className="offers-caption">
        <span>Предложения поставщиков</span>
        <span>{state === 'loaded' ? `${item.supplierCount} поставщика · ${item.warehouseCount} складов` : 'Загрузка по каноническому GUID'}</span>
      </div>
      {state === 'loading' || state === 'not_loaded' ? <div className="loading-panel compact-loading" aria-label="Загрузка предложений"><span /><span /><span /></div> : null}
      {state === 'source_error' ? (
        <div className="api-source-state" role="status" data-testid="supplier-offers-source-error">
          <strong>Предложения временно недоступны</strong>
          <span>{item.offersDiagnostic?.message ?? 'Backend не вернул подтверждённую проекцию предложений.'}</span>
          {item.offersDiagnostic?.requestId ? <small>Request ID: {item.offersDiagnostic.requestId}</small> : null}
        </div>
      ) : null}
      {state === 'loaded' && item.offers.length === 0 ? (
        <div className="api-source-state" role="status" data-testid="supplier-offers-empty">
          <strong>Доступных предложений нет</strong>
          <span>Backend не вернул supplier offers для текущих серверных сопоставлений. Это не трактуется как подтверждённый нулевой остаток.</span>
        </div>
      ) : null}
      {state === 'loaded' && item.offers.length > 0 ? (
        <div className="offers-table" role="table" aria-label="Сравнение предложений поставщиков">
          <div className="offer-header" role="row">
            <span role="columnheader">Поставщик / товар</span>
            <span role="columnheader">Склад / партия</span>
            <span role="columnheader">Закуп.</span>
            <span role="columnheader">РРЦ</span>
            <span role="columnheader">Свободно</span>
            <span role="columnheader">Ожидается</span>
            <span role="columnheader">Состояние / время</span>
          </div>
          {item.offers.map((offer) => <OfferRow key={offer.id} offer={offer} lowestId={lowestId} />)}
        </div>
      ) : null}
    </section>
  );
}

function CatalogResultCard({ item, selected, onSelect }: { item: CanonicalCatalogItem; selected: boolean; onSelect: () => void }) {
  const metadata = [item.brand, item.collection, item.unit].filter((value): value is string => Boolean(value));
  const offersLoaded = (item.offersState ?? (item.offers.length > 0 ? 'loaded' : 'not_loaded')) === 'loaded';
  const warnings = [...(item.duplicateWarning ? [item.duplicateWarning] : []), ...(item.warnings ?? [])];
  return (
    <article className={selected ? 'catalog-result-card selected' : 'catalog-result-card'} data-testid="catalog-result" data-item-id={item.id}>
      <button className="product-summary" type="button" aria-expanded={selected} onClick={onSelect}>
        <WallpaperThumb texture={item.texture} />
        <span className="product-identity">
          <span className="article">{item.article ?? 'Без артикула'}</span>
          <strong>{item.name}</strong>
          <span>{metadata.join(' · ')}</span>
          {warnings.map((warning, index) => <span className="duplicate-warning" key={`${item.id}-warning-${index}`}><AlertIcon />{warning}</span>)}
        </span>
        <span className="offer-count">{offersLoaded ? `${item.supplierCount} поставщика · ${item.warehouseCount} складов` : 'Предложения по раскрытию'}</span>
        <span className="details-label">{selected ? 'Свернуть' : 'Подробнее'} <ChevronIcon open={selected} /></span>
      </button>
      {selected ? <OffersPanel item={item} /> : null}
    </article>
  );
}

interface CatalogPageProps {
  mode?: TenantFeatureMode;
}

export function CatalogPage({ mode = 'tenant' }: CatalogPageProps) {
  const readOnly = mode === 'support-readonly';
  const location = useLocation();
  const navigate = useNavigate();
  const session = useAuthSession();
  const params = new URLSearchParams(location.search);
  const routeQuery = params.get('query') ?? 'R28003';
  const [input, setInput] = React.useState(routeQuery);
  const [result, setResult] = React.useState<CatalogSearchResult>({ query: routeQuery, items: [], mockNotice: '' });
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [searchError, setSearchError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setSearchError(null);
    repository.searchCatalog(routeQuery).then((next) => {
      if (!active) return;
      setResult(next);
      setSelectedId((previous) => selectExpandedItem(next.items, previous));
      setInput(routeQuery);
      setLoading(false);
    }).catch((error: unknown) => {
      if (!active) return;
      setLoading(false);
      if (isSessionRequiredError(error)) {
        session.expireSession();
        navigate('/auth/error?reason=session-expired', { replace: true });
        return;
      }
      setSearchError(error instanceof ApiRequestError && error.requestId
        ? `Не удалось выполнить поиск. Request ID: ${error.requestId}`
        : 'Не удалось выполнить поиск через Backend API.');
    });
    return () => { active = false; };
  }, [navigate, routeQuery, session]);

  React.useEffect(() => {
    if (!selectedId) return;
    const selected = result.items.find((item) => item.id === selectedId);
    if (!selected) return;
    const state = selected.offersState ?? (selected.offers.length > 0 ? 'loaded' : 'not_loaded');
    if (state !== 'not_loaded') return;

    let active = true;
    setResult((current) => ({
      ...current,
      items: current.items.map((item) => item.id === selectedId ? { ...item, offersState: 'loading' } : item),
    }));
    repository.loadCatalogOffers(selectedId).then((offersResult) => {
      if (!active) return;
      setResult((current) => ({
        ...current,
        items: current.items.map((item) => item.id === selectedId ? {
          ...item,
          offers: offersResult.offers,
          supplierCount: offersResult.supplierCount,
          warehouseCount: offersResult.warehouseCount,
          offersState: offersResult.state,
          offersDiagnostic: offersResult.diagnostic,
        } : item),
      }));
    }).catch((error: unknown) => {
      if (!active) return;
      if (isSessionRequiredError(error)) {
        session.expireSession();
        navigate('/auth/error?reason=session-expired', { replace: true });
        return;
      }
      setResult((current) => ({
        ...current,
        items: current.items.map((item) => item.id === selectedId ? {
          ...item,
          offersState: 'source_error',
          offersDiagnostic: {
            message: 'Не удалось загрузить supplier offers через Backend API.',
            requestId: error instanceof ApiRequestError ? error.requestId : undefined,
            status: error instanceof ApiRequestError ? error.status : undefined,
          },
        } : item),
      }));
    });
    return () => { active = false; };
  }, [navigate, result.items, selectedId, session]);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim() || 'R28003';
    const normalized = repository.mode === 'mock' ? trimmed.toUpperCase() : trimmed;
    const base = readOnly ? '/support/catalog' : '/tenant/catalog';
    navigate(`${base}?query=${encodeURIComponent(normalized)}`);
  };

  return (
    <div className="page catalog-page" data-testid="catalog-page" data-query={result.query} data-read-only={readOnly ? 'true' : 'false'} data-data-mode={repository.mode}>
      <header className="page-header compact-page-header">
        <div>
          <p className="eyebrow">КАТАЛОГ 1С · ОБОИ</p>
          <h1>Каталог</h1>
          <p>Поиск канонического товара и сравнение доступных предложений поставщиков.</p>
        </div>
        {readOnly ? <span className="readonly-chip">Только чтение</span> : null}
      </header>

      <form className="catalog-search" onSubmit={submit} role="search">
        <label className="search-field">
          <span className="sr-only">Артикул или название товара</span>
          <SearchIcon />
          <input
            value={input}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setInput(event.currentTarget.value)}
            placeholder="Артикул или название"
            autoComplete="off"
            data-testid="catalog-search-input"
          />
        </label>
        <button type="submit" className="button primary" data-testid="catalog-search-submit">Найти</button>
        <button type="button" className="button secondary desktop-filter">Бренд</button>
        <button type="button" className="button secondary desktop-filter">Коллекция</button>
        <button type="button" className="button secondary">Фильтры</button>
      </form>

      <section className="catalog-results" aria-live="polite">
        <div className="results-meta">
          <span>{loading ? 'Поиск…' : `Результаты: ${result.items.length}`}</span>
          <span>Запрос: <strong>{result.query}</strong></span>
        </div>
        {searchError ? <div className="api-source-state" role="alert" data-testid="catalog-search-error"><strong>Поиск недоступен</strong><span>{searchError}</span></div> : null}
        {!loading && !searchError && result.items.length === 0 ? <EmptyState query={result.query} /> : null}
        {!loading && !searchError ? result.items.map((item) => (
          <CatalogResultCard
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            onSelect={() => setSelectedId(item.id === selectedId && result.items.length > 1 ? null : item.id)}
          />
        )) : loading ? <div className="loading-panel" aria-label="Загрузка результатов"><span /><span /><span /></div> : null}
      </section>
    </div>
  );
}
